// ─────────────────────────────────────────────────────────────────────────────
// What a form stores, and every string the components render.
// ─────────────────────────────────────────────────────────────────────────────

import type { MediaObject, StoredMedia } from '@repletoryreach/sdk-api/media';

/**
 * A stored file, as a form holds it.
 *
 * `url` is signed and needs no credentials, so it can go straight into a
 * `<img src>`, an email, or the media header Meta fetches when it renders a
 * template. It also expires, which is why `id` travels with it: re-sign from
 * the id rather than persisting a URL that never dies, because a permanent URL
 * to a private bucket is a leak that cannot be revoked.
 *
 * A column that can only hold a string still works: pass the URL as `value` and
 * take `next?.url` in `onChange`. A signed URL from this platform carries the
 * type and filename in its own query string, so the preview survives the round
 * trip; what is lost is the id, and with it the ability to re-sign.
 */
export interface MediaValue {
  id: string;
  url: string;
  /** ISO timestamp. Absent when the value came in as a bare URL string. */
  url_expires_at?: string | null;
  mime_type: string;
  size_bytes: number;
  original_name: string | null;
}

/** Widen either service shape into the value a form keeps. */
export function toMediaValue(o: MediaObject | StoredMedia): MediaValue {
  return {
    id: o.id,
    url: o.url ?? '',
    url_expires_at: o.url_expires_at ?? null,
    mime_type: o.mime_type,
    size_bytes: o.size_bytes,
    original_name: o.original_name
  };
}

/**
 * Accept a bare URL where a form column can only hold a string.
 *
 * A signed URL from this platform carries `response-content-type` and a
 * `response-content-disposition` filename, so the type and the name are read
 * back from the URL itself rather than guessed. Our object keys are bare GUIDs
 * with no extension, so without this every stored image would come back as
 * `application/octet-stream` and preview as a document icon.
 *
 * A foreign URL falls back to its file extension.
 */
export function fromUrl(url: string): MediaValue {
  let mime: string | null = null;
  let name: string | null = null;

  const q = url.indexOf('?');
  if (q >= 0) {
    const params = new URLSearchParams(url.slice(q + 1));
    mime = params.get('response-content-type');
    const disposition = params.get('response-content-disposition') ?? '';
    name = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? null;
  }

  if (!name) name = decodeURIComponent(url.slice(0, q < 0 ? undefined : q).split('/').pop() ?? '') || null;
  return {
    id: '', url, url_expires_at: null,
    mime_type: mime || guessMime(name),
    size_bytes: 0,
    original_name: name
  };
}

/** Normalise whatever a caller passes as `value`. */
export function normaliseValue(v: MediaValue | string | null | undefined): MediaValue | null {
  if (!v) return null;
  return typeof v === 'string' ? fromUrl(v) : v;
}

const EXT_MIME: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  mp4: 'video/mp4', '3gp': 'video/3gpp', mov: 'video/quicktime',
  mp3: 'audio/mpeg', aac: 'audio/aac', ogg: 'audio/ogg', amr: 'audio/amr',
  pdf: 'application/pdf', doc: 'application/msword', txt: 'text/plain',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

function guessMime(name: string | null): string {
  const ext = name?.split('.').pop()?.toLowerCase() ?? '';
  return EXT_MIME[ext] ?? 'application/octet-stream';
}

/** Broad kind, for choosing a preview. */
export type MediaKind = 'image' | 'video' | 'audio' | 'document';

export function kindOf(mime: string): MediaKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

/** Human file size. Kilobyte as 1024 bytes, the convention every OS shows. */
export function formatBytes(bytes: number): string {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let u = 0;
  while (n >= 1024 && u < units.length - 1) { n /= 1024; u++; }
  return `${n < 10 && u > 0 ? n.toFixed(1) : Math.round(n)} ${units[u]}`;
}

/**
 * Every string the components render.
 *
 * English defaults, overridable one key at a time, following the `Spotlight`
 * convention in sdk-ui: the catalogues live in the apps, so the package must
 * not own copy that has to be translated.
 */
export interface FileUploadLabels {
  prompt: string;
  hint: string;
  library: string;
  libraryTitle: string;
  librarySearch: string;
  libraryEmpty: string;
  libraryLoadMore: string;
  uploading: string;
  cancel: string;
  retry: string;
  replace: string;
  remove: string;
  rename: string;
  renameTitle: string;
  renamePlaceholder: string;
  save: string;
  copy: string;
  copied: string;
  open: string;
  view: string;
  select: string;
  deleteFile: string;
  deleteConfirm: string;
  filterAll: string;
  filterImage: string;
  filterVideo: string;
  filterDocument: string;
  errorTooLarge: string;
  errorType: string;
  errorInfected: string;
  errorScanUnavailable: string;
  errorNotConfigured: string;
  errorGeneric: string;
}

export const DEFAULT_LABELS: FileUploadLabels = {
  prompt: 'Drop a file here, or click to browse',
  hint: '',
  library: 'Choose from library',
  libraryTitle: 'Media library',
  librarySearch: 'Search by filename',
  libraryEmpty: 'Nothing here yet. Upload a file to see it listed.',
  libraryLoadMore: 'Load more',
  uploading: 'Uploading',
  cancel: 'Cancel',
  retry: 'Retry',
  replace: 'Replace',
  remove: 'Remove',
  rename: 'Rename',
  renameTitle: 'Rename file',
  renamePlaceholder: 'Filename',
  save: 'Save',
  copy: 'Copy link',
  copied: 'Copied',
  open: 'Open',
  view: 'View',
  select: 'Select',
  deleteFile: 'Delete permanently',
  deleteConfirm: 'Delete this file for everyone? Anything still using it will break.',
  filterAll: 'All',
  filterImage: 'Images',
  filterVideo: 'Video',
  filterDocument: 'Documents',
  errorTooLarge: 'That file is too large.',
  errorType: 'That file type is not accepted.',
  errorInfected: 'That file failed the virus scan and was not stored.',
  errorScanUnavailable: 'The virus scanner is unreachable. Try again shortly.',
  errorNotConfigured: 'File storage is not configured. An operator must set it up under Settings, Integrations.',
  errorGeneric: 'The upload failed.'
};
